/**
 * Refactor proposals (doc 147 §6.6 E3). v1 ships heuristic-based
 * proposals derived from the same signals the lint engine uses; v2 will
 * pipe the candidates to Ask Atlas (Gemini) for natural-language draft
 * PR descriptions. The "AI proposed" flow is intentionally a separate
 * follow-up so the heuristic baseline is reviewable on its own.
 */
import type { AtlasManifest } from '@atlas-manifest/types';
// Reuse the lint engine's validators so proposals stay in lockstep with
// the rule heuristics.
import { godEntity, unbridgedCrossDomain, refCycle } from '@atlas-manifest/lint';

export type ProposalKind =
  | 'split-god-entity'
  | 'add-bridge-tag'
  | 'break-cycle';

export interface RefactorProposal {
  kind: ProposalKind;
  /** Primary entity this proposal targets (source of the problematic relationship). */
  entityId: string;
  /** Full lint-finding ref (e.g. "A → B" for bridge/cycle, entity id for god-entity).
   *  Unique per lint finding — use as React key via proposalId() to avoid duplicates
   *  when the same source entity appears in multiple findings of the same kind. */
  ref: string;
  title: string;
  rationale: string;
  /** 0..1 — heuristic confidence; sort descending to show top candidates first. */
  confidence: number;
}

const KIND_CONFIDENCE: Record<ProposalKind, number> = {
  'split-god-entity': 0.85,
  'add-bridge-tag': 0.7,
  'break-cycle': 0.5,
};

export function buildRefactorProposals(manifest: AtlasManifest): RefactorProposal[] {
  const proposals: RefactorProposal[] = [];

  for (const f of godEntity(manifest)) {
    proposals.push({
      kind: 'split-god-entity',
      entityId: f.ref,
      ref: f.ref,
      title: `Split ${f.ref} into smaller entities`,
      rationale: f.message,
      confidence: KIND_CONFIDENCE['split-god-entity'],
    });
  }

  for (const f of unbridgedCrossDomain(manifest)) {
    const [src] = f.ref.split(' → ');
    proposals.push({
      kind: 'add-bridge-tag',
      entityId: src ?? '',
      ref: f.ref,
      title: `Tag ${f.ref} as an intentional cross-domain bridge`,
      rationale: f.message,
      confidence: KIND_CONFIDENCE['add-bridge-tag'],
    });
  }

  for (const f of refCycle(manifest)) {
    const first = f.ref.split(' → ')[0] ?? '';
    proposals.push({
      kind: 'break-cycle',
      entityId: first,
      ref: f.ref,
      title: `Break reference cycle: ${f.ref}`,
      rationale: f.message,
      confidence: KIND_CONFIDENCE['break-cycle'],
    });
  }

  return proposals.sort((a, b) => b.confidence - a.confidence);
}
